import z from "zod";
import { FieldError, FieldErrors } from "react-hook-form";
import { isNumericString } from "@/utils/string-utils";
import { CHALK_DURATION_REGEX } from "@/utils/date-time";
import toast from "react-hot-toast";

export const EMAIL_REGEX = new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i);

export const CHALK_DURATION_SCHEMA = z
  .string()
  .regex(CHALK_DURATION_REGEX, "Invalid duration format. Use format like: 1h, 30m, 1d2h30m")
  .optional()
  .or(z.literal("").transform(() => undefined));

export interface NumericTextFieldSchemaOptions {
  fieldName?: string;
  integer?: boolean;
  min?: number;
  max?: number;
}

export const emptyStringToUndefinedValueAs = (value: unknown): unknown => {
  return typeof value === "string" && value === "" ? undefined : value;
};

const INTEGER_TEXT_INPUT_REGEX = /^-?\d+$/;
const NUMBER_TEXT_INPUT_REGEX = /^-?(?:\d+\.?\d*|\.\d+)$/;

const normalizeNumericTextInput = (value: unknown): unknown => {
  if (typeof value === "number") {
    return value;
  }

  if (value == null) {
    return undefined;
  }

  if (typeof value !== "string") {
    return value;
  }

  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
};

const makeNumericTextFieldSchemaBase = ({
  fieldName = "Value",
  integer = true,
  min,
  max,
}: NumericTextFieldSchemaOptions = {}) => {
  const numberTypeName = integer ? "integer" : "number";
  const regex = integer ? INTEGER_TEXT_INPUT_REGEX : NUMBER_TEXT_INPUT_REGEX;

  return z
    .union([z.string(), z.number(), z.undefined()], {
      invalid_type_error: `${fieldName} must be a valid ${numberTypeName}`,
      required_error: `${fieldName} is required`,
    })
    .transform((value, ctx) => {
      if (value == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${fieldName} is required`,
        });
        return z.NEVER;
      }

      if (typeof value === "number") {
        if (!Number.isFinite(value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${fieldName} must be a valid ${numberTypeName}`,
          });
          return z.NEVER;
        }
        return value;
      }

      if (!regex.test(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${fieldName} must be a valid ${numberTypeName}`,
        });
        return z.NEVER;
      }

      return integer ? Number.parseInt(value, 10) : Number.parseFloat(value);
    })
    .refine((value) => min == null || value >= min, {
      message: `${fieldName} must be at least ${min}`,
    })
    .refine((value) => max == null || value <= max, {
      message: `${fieldName} must be at most ${max}`,
    });
};

/**
 * Use with a text input in react-hook-form. Accepts string input from the form and
 * number input from backend/default values, then normalizes to a number.
 */
export const numericTextFieldSchema = (options: NumericTextFieldSchemaOptions = {}) =>
  z.preprocess(normalizeNumericTextInput, makeNumericTextFieldSchemaBase(options));

/**
 * Optional variant of `numericTextFieldSchema`. Empty strings are parsed to `undefined`.
 */
export const optionalNumericTextFieldSchema = (options: NumericTextFieldSchemaOptions = {}) =>
  z.preprocess(normalizeNumericTextInput, makeNumericTextFieldSchemaBase(options).optional());

/**
 * Optional text-input schema that accepts backend numbers, missing values, and empty strings.
 * Empty strings and nullish values normalize to `undefined`; numbers normalize to strings.
 */
export const optionalTextInputStringSchema = () =>
  z.preprocess((value) => {
    if (value == null || value === "") {
      return undefined;
    }

    if (typeof value === "number") {
      return `${value}`;
    }

    return value;
  }, z.string().optional());

export const makeZodJsonStringSchema = <T extends z.ZodRawShape>(
  parseSchema:
    | z.ZodAny
    | z.ZodObject<T>
    | z.ZodArray<z.ZodUnion<[z.ZodTypeAny, ...z.ZodTypeAny[]]>, "many">
    | z.ZodArray<z.ZodObject<T>, "atleastone">
    | z.ZodEffects<z.ZodObject<T>>
    | z.ZodEffects<z.ZodArray<z.ZodObject<T>, "atleastone">>,
): z.ZodEffects<z.ZodString, string, string> => {
  return z.string().superRefine((val, ctx) => {
    try {
      // first, verify that the JSON can even be parsed (try-catch)
      const jsonParsed = JSON.parse(val);
      // then, verify that the parsed JSON conforms to the SpecsConfig form schema
      const parsed = parseSchema.safeParse(jsonParsed);
      if (!parsed.success) {
        parsed.error.errors.forEach((error) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: error.message,
          });
        });
        return false;
      }
      return true;
    } catch (e) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: (e as z.ZodIssue).message,
      });
      return false;
    }
  });
};

export const getFieldFromSchema = <T extends z.ZodRawShape>(
  schema: z.ZodObject<T>,
  path: string,
  unionTypeName?: string,
): z.ZodType => {
  const splitPath = path.split(".");

  const recursivelyIndexIntoSchema = (schema: z.ZodTypeAny, pathArr: string[]): z.ZodType => {
    // Even if we are at the base field, we would like to unwrap ZodEffects
    // These are just wrappers around the underlying types
    if (schema instanceof z.ZodEffects) {
      return recursivelyIndexIntoSchema(schema._def.schema, pathArr);
    }

    if (pathArr.length === 0) {
      return schema;
    }

    if (schema instanceof z.ZodOptional || schema instanceof z.ZodNullable) {
      return recursivelyIndexIntoSchema(schema.unwrap(), pathArr);
    }

    if (
      (schema instanceof z.ZodUnion || schema instanceof z.ZodDiscriminatedUnion) &&
      !!unionTypeName
    ) {
      // only 1 level of checking with this
      const matchingSchema = schema.options.find(
        (option: z.ZodAny) =>
          option instanceof z.ZodObject &&
          // supports discriminator names `kind` and `case`
          ((option.shape?.kind instanceof z.ZodLiteral &&
            option.shape.kind.value === unionTypeName) ||
            (option.shape?.case instanceof z.ZodLiteral &&
              option.shape.case.value === unionTypeName)),
      );
      if (!matchingSchema) {
        throw new Error(`Could not find matching union type for '${unionTypeName}'`);
      }
      return recursivelyIndexIntoSchema(matchingSchema, pathArr);
    }

    const [nextPath, ...restOfPath] = pathArr;

    if (isNumericString(nextPath) && schema instanceof z.ZodArray) {
      return recursivelyIndexIntoSchema(schema.element, restOfPath);
    }

    if (schema instanceof z.ZodObject) {
      return recursivelyIndexIntoSchema(schema.shape[nextPath], restOfPath);
    }

    throw new Error(
      `Expected ZodEffect, ZodArray, ZodOptional, or ZodObject, got ${schema.constructor.name}`,
    );
  };

  return recursivelyIndexIntoSchema(schema, splitPath);
};

const isFieldError = (maybeError: FieldError | FieldErrors): maybeError is FieldError => {
  return "message" in maybeError && typeof maybeError.message === "string";
};

export const collectNestedFormErrors = (errors: FieldErrors): string[] => {
  const errorMessages: string[] = [];
  for (const key in errors) {
    const subError = errors[key];
    if (!subError) {
      continue;
    }
    if (isFieldError(subError)) {
      if (subError.message) {
        errorMessages.push(`${key}: ${subError.message}`);
      }
    } else {
      errorMessages.push(...collectNestedFormErrors(subError));
    }
  }

  return errorMessages;
};

// For use in z.transform. Strips away a value if the value is falsy, useful for unsetting a field
// without setting it accidentally to ""
export const stripIfFalsy = <T extends number | string | boolean>(
  x: T | null | undefined,
): T | undefined => x || undefined;

// For use in z.transform. Strips away a value if the value is null
export const stripIfNull = <T>(x: T | null | undefined): T | undefined => x ?? undefined;

export type KeyValueEntry = { key: string; value: string };

export const stripEmptyEntries = (entries: KeyValueEntry[]): KeyValueEntry[] => {
  return entries.filter(({ key, value }) => !!key && !!value);
};

export const formatZodIssue = (error: z.ZodIssue, withPath: boolean = true): string => {
  const path = withPath ? error.path.join(".") : "";
  if (error.code === z.ZodIssueCode.invalid_union) {
    const errors = error.unionErrors
      .map((error: z.ZodError, idx: number) => {
        const message = error.issues
          .map((issue: z.ZodIssue) => formatZodIssue(issue, true))
          .join(", ");
        return `Option ${idx + 1}: ${message}`;
      })
      .join("\n");
    return `Could not resolve union type, follow one of the following options:\n${errors}`;
  }
  if (path === "") {
    return error.message;
  }
  const message = `${path}: ${error.message}`;
  return message;
};

/**
 * Utility function that takes a list of potentially falsy path parts and outputs a valid path to
 * an object field
 *
 * @param parts the list of parts to combine
 */
export const joinPath = (...parts: Array<string | null | undefined>): string => {
  return parts.reduce<string>((stringBuilder, part) => {
    if (!part) {
      return stringBuilder;
    }

    return `${stringBuilder && `${stringBuilder}.`}${part}`;
  }, "");
};

export const isFieldDirty = <T>(dirtyFields: T): boolean => {
  if (typeof dirtyFields === "boolean") {
    return dirtyFields;
  } else if (dirtyFields == null) {
    return false;
  } else if (typeof dirtyFields == "object") {
    if (Array.isArray(dirtyFields)) {
      return dirtyFields.some(isFieldDirty);
    } else {
      return Object.values(dirtyFields).some(isFieldDirty);
    }
  }

  return Boolean(dirtyFields);
};

export const genericErrorToaster =
  (formName = "form") =>
  (errors: FieldErrors) => {
    toast.error(`Error submitting ${formName}: ${collectNestedFormErrors(errors).join(", ")}`);
  };
